import { useEffect, useRef, useState } from 'react';
import { NARRATION_PROVIDER_LABELS, type NarrationProviderCode } from './constants';
import { createPortal } from 'react-dom';
import { listSceneNarrations } from '../../../features/scenarios/api';
import { requestDeleteTemporary } from '../../../features/materials/api';
import { resolveAudioUrl } from './narrationUtils';
import { formatDateTimeShort as formatDate } from '../../../utils/formatters';
import { useLockScrollOutside } from '../../../hooks/useLockScrollOutside';
import styles from './ScenarioGenerator.module.css';

/** ナレーション履歴セレクタ */
/** 履歴行のプロバイダ表示名。未記録 (旧データ) や未知の値は ElevenLabs 扱い */
function narrationProviderLabel(provider?: string): string {
  return NARRATION_PROVIDER_LABELS[(provider as NarrationProviderCode) ?? 'elevenlabs'] ?? NARRATION_PROVIDER_LABELS.elevenlabs;
}

export function NarrationHistorySelector({
  sceneId,
  currentMaterialId,
  onSelect,
  voiceNameMap,
  selectedVoiceName,
  selectedDurationSeconds,
  excluded,
  onToggleExcluded,
}: {
  sceneId: string;
  currentMaterialId?: string;
  onSelect: (materialId: string, voiceId?: string, durationSeconds?: number) => void;
  voiceNameMap: Record<string, string>;
  selectedVoiceName?: string;
  selectedDurationSeconds?: number;
  /** 「採用しない」トグル (プレビュー・エクスポートから除外)。ポップアップ最上部に行を出す */
  excluded?: boolean;
  onToggleExcluded?: (excluded: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [narrations, setNarrations] = useState<Array<{
    id: string;
    created_at: string;
    is_current: boolean;
    voice_id?: string;
    provider?: string; // 'elevenlabs' | 'gemini' | 'fish' (旧データは elevenlabs)
    language?: string; // 発音言語 'ja' | 'en' (旧データは ja)
    file_size_bytes?: number;
    duration_seconds?: number;
    preview_url?: string;
    source_url?: string;
    storage_bucket?: string;
    storage_path?: string;
    resolvedUrl?: string;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [currentDuration, setCurrentDuration] = useState<number | undefined>(undefined);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const togglePlay = async (narration: typeof narrations[number]) => {
    if (playingId === narration.id) {
      stopAudio();
      return;
    }
    stopAudio();

    // resolvedUrlがあればそれを使う、なければ解決する
    let url = narration.resolvedUrl;
    if (!url) {
      url = await resolveAudioUrl({
        id: narration.id,
        preview_url: narration.preview_url,
        source_url: narration.source_url,
        storage_bucket: narration.storage_bucket,
        storage_path: narration.storage_path,
      }) || undefined;
      if (url) {
        // 解決したURLをキャッシュ
        setNarrations(prev => prev.map(n => n.id === narration.id ? { ...n, resolvedUrl: url } : n));
      }
    }
    if (!url) return;

    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingId(narration.id);
    audio.play();
    audio.onended = () => stopAudio();
    audio.onerror = () => stopAudio();
  };

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        stopAudio();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // ポップアップは開いた位置に fixed で置くため、開いている間は背景を動かさない
  useLockScrollOutside(open, dropdownRef);

  const updateDropdownPos = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.top,
        left: rect.left,
      });
    }
  };

  const fetchHistory = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const items = (await listSceneNarrations(sceneId)).reverse();
      setNarrations(items);
      if (currentMaterialId) {
        const current = items.find((n: { id: string; duration_seconds?: number }) => n.id === currentMaterialId);
        if (current?.duration_seconds != null) {
          setCurrentDuration(current.duration_seconds);
        }
      }
      updateDropdownPos();
      setOpen(true);
    } catch {
      // 取得失敗
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (materialId: string) => {
    if (playingId === materialId) stopAudio();
    try {
      await requestDeleteTemporary(materialId);
      setNarrations(prev => prev.filter(n => n.id !== materialId));
    } catch {
      // 削除失敗
    }
  };


  const dropdownContent = (items: typeof narrations) => (
    <div
      ref={dropdownRef}
      className={`modal-surface ${styles.narrationHistoryDropdown}`}
      style={{
        position: 'fixed',
        bottom: `${window.innerHeight - dropdownPos.top + 4}px`,
        left: `${dropdownPos.left}px`,
      }}
    >
      {/* 最上部: 「採用しない」行 (右寄せ・非活性色)。テイクは保持したままプレビュー
          MP4・エクスポートから外す。採用に戻すUIは無く、いずれかのテイクを選ぶと戻る。 */}
      {onToggleExcluded && currentMaterialId && !excluded && (
        <div
          className={styles.narrationHistoryItem}
          style={{ borderBottom: 'var(--modal-divider, 1px solid rgba(255, 255, 255, 0.08))' }}
        >
          <button
            type="button"
            className={styles.narrationHistorySelect}
            style={{ justifyContent: 'flex-end', color: 'rgba(245, 241, 230, 0.45)' }}
            onClick={() => {
              onToggleExcluded(true);
              stopAudio();
              setOpen(false);
            }}
            title="プレビュー動画やエクスポートに含めません。テイクを選ぶと採用に戻ります"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              採用しない
            </span>
          </button>
        </div>
      )}
      {items.length > 0 ? items.map((n) => (
        <div
          key={n.id}
          className={`${styles.narrationHistoryItem} ${n.id === currentMaterialId ? styles.narrationHistoryCurrent : ''}`}
        >
          <button
            type="button"
            className={styles.narrationHistorySelect}
            onClick={() => {
              onSelect(n.id, n.voice_id, n.duration_seconds);
              if (n.duration_seconds != null) setCurrentDuration(n.duration_seconds);
              stopAudio();
              setOpen(false);
            }}
          >
            <span className={styles.narrationHistoryInfo}>
              <span>{formatDate(n.created_at)}</span>
              {(() => {
                // Gemini は voice_id がそのまま音声名 (Kore 等) なので、
                // 名前マップに無くても voice_id で表示できる。
                const voiceName = n.voice_id
                  ? voiceNameMap[n.voice_id] ?? (n.provider && n.provider !== 'elevenlabs' ? n.voice_id : undefined)
                  : undefined;
                return voiceName && (
                  <span className={styles.narrationHistoryVoice}>{voiceName}</span>
                );
              })()}
              {n.duration_seconds != null && (
                <span className={styles.narrationHistoryDuration}>
                  {n.duration_seconds.toFixed(2)}秒（{narrationProviderLabel(n.provider)}）
                </span>
              )}
              {n.language === 'en' && (
                <span className={styles.narrationHistoryLangEn} title="英語で発音">EN</span>
              )}
            </span>
            {n.id === currentMaterialId && <span className={styles.narrationHistoryBadge}>選択中</span>}
          </button>
          {(n.preview_url || n.source_url || n.storage_path) && (
            <button
              type="button"
              className={styles.narrationHistoryPlay}
              onClick={(e) => {
                e.stopPropagation();
                togglePlay(n);
              }}
              title={playingId === n.id ? '停止' : '再生'}
            >
              {playingId === n.id ? (
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
                  <rect x="5.5" y="5.5" width="5" height="5" rx="0.5" fill="currentColor" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
                  <polygon points="6.5,4.5 12,8 6.5,11.5" fill="currentColor" />
                </svg>
              )}
            </button>
          )}
          <button
            type="button"
            className={styles.narrationHistoryDelete}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(n.id);
            }}
            title="削除"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M5.5 1.5h5M2.5 4h11M6 4v8.5M10 4v8.5M3.5 4l.5 10.5a1 1 0 001 .5h6a1 1 0 001-.5L12.5 4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )) : (
        <div className={styles.narrationHistoryItem} style={{ cursor: 'default', opacity: 0.6 }}>
          履歴がありません
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'inline-block' }}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.narrationHistoryButton}
        onClick={fetchHistory}
        disabled={loading}
        title="過去のナレーション履歴"
      >
        {loading ? (
          <span className={styles.spinnerSmall} />
        ) : (
          <>
            {excluded ? (
              <span className={styles.narrationHistoryDurationLabel}>採用なし</span>
            ) : (
              <>
                {selectedVoiceName && <span className={styles.narrationHistoryVoiceLabel}>{selectedVoiceName}</span>}
                {(currentDuration ?? selectedDurationSeconds) != null && <span className={styles.narrationHistoryDurationLabel}>{currentDuration ?? selectedDurationSeconds}秒</span>}
              </>
            )}
            {(narrations.length === 0 || narrations.length > 1 || excluded) && <span className={styles.narrationHistoryCaret}>&#9662;</span>}
          </>
        )}
      </button>
      {open && createPortal(dropdownContent(narrations), document.body)}
    </div>
  );
}
